import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, CreditCard, CheckCircle, Check, Wallet, Bitcoin, ChevronRight, Loader2, AlertCircle, Tag, ExternalLink, Clock } from 'lucide-react';
import { useLanguage } from '../../../contexts/languages/LanguageContext';
import { useUserData } from '../../../hooks/websocket/useUserData';
import { useProducts } from '../../../contexts/backend/ProductsContext';
import { useCart } from '../../../contexts/CartContext';
import { useWebSocket } from '../../../contexts/backend/WebSocketContext';
import { fetchQuote, startCheckout } from '../../../services/checkout';
import type { QuoteResponse, CheckoutAddress } from '../../../services/checkout';
import CartList from './CartList';
import type { PurchaseProduct, FormDataState } from '../types';
import { useGeoLocation } from '../../../hooks/useGeoLocation';

interface CartItem {
  id: string;
  name: string;
  price: number;
  currency: string;
  type: string;
  quantity: number;
  [key: string]: any;
}

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems?: CartItem[];
  onRemoveItem?: (itemId: string) => void;
  onUpdateQuantity?: (itemId: string, quantity: number) => void;
  onPurchaseSuccess?: () => void;
  onClearCart?: () => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  isOpen,
  onClose,
  cartItems = [],
  onRemoveItem = () => {},
  onUpdateQuantity,
  onPurchaseSuccess,
  onClearCart
}) => {
  const { t, language, countries } = useLanguage();
  const { user } = useUserData();
  const { getProductById } = useProducts();
  const { openPurchaseModal, closePurchaseModal } = useCart();
  const { data: wsData } = useWebSocket();
  const { isEU } = useGeoLocation();
  const [currentStep, setCurrentStep] = useState<0 | 1 | 2 | 3>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<'EUR' | 'USD'>(isEU ? 'EUR' : 'USD');
  const currencySymbol = selectedCurrency === 'EUR' ? '€' : '$';
  const [useWalletBalance, setUseWalletBalance] = useState(false);

  const [quoteData, setQuoteData] = useState<QuoteResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const checkoutRefRef = useRef(crypto.randomUUID());

  const walletAvailableForCurrency = useMemo(() => {
    const balance = wsData.walletBalances.find(
      b => b.currency.toUpperCase() === selectedCurrency
    );
    return balance ? parseFloat(balance.available_balance) : 0;
  }, [wsData.walletBalances, selectedCurrency]);

  const formatPrice = (value: number | undefined | null): string => {
    if (value == null) return '0';
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2);
  };

  const hasMultipleItems = cartItems.length > 1;
  const singleItem = cartItems.length === 1 ? cartItems[0] : null;
  const shouldShowCartList = hasMultipleItems || (singleItem && singleItem.quantity > 1);
  const isExtensionType = (type?: string) => type === 'Extension' || type === 'FundedFeeExtension';

  const fullProduct = useMemo(() => {
    if (!singleItem?.id) return null;
    return getProductById(singleItem.id);
  }, [singleItem?.id, getProductById]);

  const hasUsdPrices = useMemo(() => {
    return cartItems.every(item => {
      const product = getProductById(item.id);
      return product?.prices?.some(p => p.currency_code === 'USD');
    });
  }, [cartItems, getProductById]);

  const activeVolumeDiscount = useMemo(() => {
    if (!fullProduct || !singleItem) return null;
    const qty = singleItem.quantity;
    const volumeDiscount = fullProduct.volume_discounts?.find(vd => vd.currency_code === selectedCurrency);
    if (!volumeDiscount?.tiers?.length) return null;
    const applicableTier = [...volumeDiscount.tiers]
      .sort((a, b) => b.min_qty - a.min_qty)
      .find(tier => qty >= tier.min_qty);
    return applicableTier || null;
  }, [fullProduct, singleItem?.quantity, selectedCurrency]);

  const discountAmount = useMemo(() => {
    if (!fullProduct?.prices) return 0;
    const priceData = fullProduct.prices.find(p => p.currency_code === selectedCurrency);
    if (!priceData) return 0;
    if (activeVolumeDiscount) {
      return priceData.price - activeVolumeDiscount.discounted_price;
    }
    const hasDiscount = priceData.discounted_price !== null && priceData.discounted_price !== priceData.price;
    return hasDiscount ? (priceData.price - (priceData.discounted_price || priceData.price)) : 0;
  }, [fullProduct, selectedCurrency, activeVolumeDiscount]);

  const discountPercentage = useMemo(() => {
    if (!fullProduct?.prices) return 0;
    const priceData = fullProduct.prices.find(p => p.currency_code === selectedCurrency);
    if (!priceData) return 0;
    if (activeVolumeDiscount) {
      return Math.round(((priceData.price - activeVolumeDiscount.discounted_price) / priceData.price) * 100);
    }
    if (!priceData.discounted_price || priceData.discounted_price === priceData.price) return 0;
    return Math.round(((priceData.price - priceData.discounted_price) / priceData.price) * 100);
  }, [fullProduct, selectedCurrency, activeVolumeDiscount]);

  const originalPrice = useMemo(() => {
    if (!fullProduct?.prices) return null;
    const priceData = fullProduct.prices.find(p => p.currency_code === selectedCurrency);
    if (!priceData) return null;
    if (activeVolumeDiscount) return priceData.price;
    const hasDiscount = priceData.discounted_price !== null && priceData.discounted_price !== priceData.price;
    return hasDiscount ? priceData.price : null;
  }, [fullProduct, selectedCurrency, activeVolumeDiscount]);

  const displayProduct = singleItem ? {
    id: singleItem.id,
    name: singleItem.name,
    price: singleItem.price,
    discount: discountAmount,
    category: 'Evaluation',
    subtitle: singleItem.name.split(' ')[1] || '',
    description: `Evaluation account with ${singleItem.name}`,
    couponPossible: true,
    couponCode: '',
    referralId: ''
  } as PurchaseProduct : null;

  const isFundedFee = singleItem?.type === 'FundedFee' || singleItem?.type === 'Funded Fee' || singleItem?.name === 'Funded Fee';

  const productDetails = useMemo(() => {
    if (!fullProduct?.account_config) return null;

    const config = fullProduct.account_config;
    const nameParts = fullProduct.name.split(' ');
    const sizeString = nameParts[1] || '';

    return {
      size: sizeString,
      initialBalance: config.initial_balance?.toLocaleString('en-US') || '',
      profitTarget: `$${config.targets_profit?.toLocaleString('en-US') || '0'}`,
      maxLoss: `$${config.risk_max_loss?.toLocaleString('en-US') || '0'}`,
      profitSplit: `${(config.profit_split * 100).toFixed(0)}%`,
      minTradingDays: config.min_trading_days?.toString() || '0',
      contractLimits: {
        minis: config.contract_limits_minis || 0,
        micros: config.contract_limits_micros || 0
      }
    };
  }, [fullProduct]);

  const [formData, setFormData] = useState<FormDataState>({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    country: '',
    address: '',
    postalCode: '',
    city: '',
    tradingSystem: '',
    paymentMethod: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    paypalEmail: '',
    cryptoType: 'bitcoin',
    acceptTerms: false,
    acceptRisks: false,
  });

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const filteredCountries = useMemo(
    () => countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase())),
    [countrySearch, countries]
  );

  const paymentProviderMap: Record<string, 'stripe' | 'paypal' | 'bitpay' | 'wallet'> = {
    card: 'stripe',
    paypal: 'paypal',
    crypto: 'bitpay',
  };

  const requestQuote = useCallback(async (coupon?: string | null, walletOpt?: boolean) => {
    if (cartItems.length === 0) return;
    setQuoteLoading(true);
    setQuoteError(null);
    const shouldUseWallet = walletOpt ?? useWalletBalance;
    try {
      const items = cartItems.map(item => {
        const configuration: Record<string, string> = {
          backend_system: formData.tradingSystem,
        };
        if ((isExtensionType(item.type) || item.type === 'Reset') && item.accountNumber) {
          configuration.account_identifier = item.accountNumber;
          if (item.tradingAccountId) {
            configuration.trading_account_id = item.tradingAccountId;
          }
        }
        return {
          productId: item.id,
          quantity: item.quantity,
          billingMode: 'one_time' as const,
          configuration,
        };
      });
      const data = await fetchQuote({
        currency: selectedCurrency,
        items,
        ...(coupon ? { couponCode: coupon } : {}),
        ...(shouldUseWallet ? { useWalletBalance: true } : {}),
      });
      setQuoteData(data);
    } catch (err: any) {
      const raw = err?.response?.data?.error || err?.message || 'Unknown error';
      const isCouponError = /promotion code|coupon/i.test(raw);
      const isBlacklistError = /blacklist/i.test(raw);
      setQuoteError(isCouponError ? t.purchaseModal.invalidCoupon : isBlacklistError ? t.purchaseModal.blacklistedMessage : raw);
    } finally {
      setQuoteLoading(false);
    }
  }, [cartItems, selectedCurrency, formData.tradingSystem, useWalletBalance]);

  const geoDefaultCurrency = isEU ? 'EUR' : 'USD';
  const geoSyncedRef = useRef(false);

  useEffect(() => {
    if (!geoSyncedRef.current && isOpen) {
      geoSyncedRef.current = true;
      setSelectedCurrency(geoDefaultCurrency);
    }
    if (!isOpen) {
      geoSyncedRef.current = false;
    }
  }, [isOpen, geoDefaultCurrency]);

  useEffect(() => {
    if (!hasUsdPrices && selectedCurrency === 'USD') {
      setSelectedCurrency('EUR');
    }
  }, [hasUsdPrices, selectedCurrency]);

  useEffect(() => {
    setUseWalletBalance(false);
  }, [selectedCurrency]);

  useEffect(() => {
    if (currentStep === 3 && cartItems.length > 0) {
      requestQuote(appliedCoupon);
    }
  }, [cartItems, selectedCurrency, formData.tradingSystem]);

  // Sync CartContext state with modal open/close
  useEffect(() => {
    if (isOpen) {
      openPurchaseModal();
    } else {
      closePurchaseModal();
    }
  }, [isOpen, openPurchaseModal, closePurchaseModal]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setAppliedCoupon(couponCode.trim());
    await requestQuote(couponCode.trim());
  };

  const handleRemoveCoupon = async () => {
    setAppliedCoupon(null);
    setCouponCode('');
    await requestQuote(null);
  };

  const resetModal = () => {
    const isDesktop = window.innerWidth >= 1024;
    setCurrentStep(isDesktop ? (user ? 2 : 1) : 0);
    setIsProcessing(false);
    setIsCompleted(false);
    setCheckoutUrl(null);
    setIsCountryOpen(false);
    setCountrySearch('');
    setSelectedCurrency(isEU ? 'EUR' : 'USD');
    setCouponCode('');
    setAppliedCoupon(null);
    setQuoteData(null);
    setQuoteError(null);
    setQuoteLoading(false);
    setUseWalletBalance(false);
    checkoutRefRef.current = crypto.randomUUID();
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      country: '',
      address: '',
      postalCode: '',
      city: '',
      tradingSystem: '',
      paymentMethod: 'card',
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: '',
      paypalEmail: '',
      cryptoType: 'bitcoin',
      acceptTerms: false,
      acceptRisks: false,
    });
  };

  const handleClose = () => {
    // Clear cart only if there's exactly one product with quantity 1
    const shouldClearCart = cartItems.length === 1 && cartItems[0].quantity === 1;
    if (shouldClearCart && onClearCart) {
      onClearCart();
    }
    resetModal();
    onClose();
  };

  const getItemPriceForCurrency = (itemId: string, fallbackPrice: number, quantity: number = 1) => {
    const product = getProductById(itemId);
    if (!product?.prices) return fallbackPrice;
    const priceData = product.prices.find(p => p.currency_code === selectedCurrency);
    if (!priceData) return fallbackPrice;

    const volumeDiscount = product.volume_discounts?.find(vd => vd.currency_code === selectedCurrency);
    if (volumeDiscount?.tiers?.length) {
      const applicableTier = [...volumeDiscount.tiers]
        .sort((a, b) => b.min_qty - a.min_qty)
        .find(tier => quantity >= tier.min_qty);
      if (applicableTier) {
        return applicableTier.discounted_price;
      }
    }

    const hasDiscount = priceData.discounted_price !== null && priceData.discounted_price !== priceData.price;
    return hasDiscount ? (priceData.discounted_price || priceData.price) : priceData.price;
  };

  const getSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const price = getItemPriceForCurrency(item.id, item.price, item.quantity);
      return total + (price || 0) * item.quantity;
    }, 0);
  };
  const getTotal = () => getSubtotal();

  const volumeDiscountTotalSavings = useMemo(() => {
    let totalSavings = 0;
    for (const item of cartItems) {
      const product = getProductById(item.id);
      if (!product?.prices || !product.volume_discounts?.length) continue;
      const priceData = product.prices.find(p => p.currency_code === selectedCurrency);
      if (!priceData) continue;
      const volumeDiscount = product.volume_discounts.find(vd => vd.currency_code === selectedCurrency);
      if (!volumeDiscount?.tiers?.length) continue;
      const applicableTier = [...volumeDiscount.tiers]
        .sort((a, b) => b.min_qty - a.min_qty)
        .find(tier => item.quantity >= tier.min_qty);
      if (applicableTier) {
        totalSavings += (priceData.price - applicableTier.discounted_price) * item.quantity;
      }
    }
    return totalSavings;
  }, [cartItems, selectedCurrency, getProductById]);

  const hasAccountProducts = useMemo(() => {
    return cartItems.some(item => item.type === 'Account');
  }, [cartItems]);

  const isTradingSystemRequired = hasAccountProducts && !formData.tradingSystem;

  const isBlacklistedForEval = user?.is_blacklisted && hasAccountProducts;

  // Pre-fill user data and set initial step when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // On desktop (lg breakpoint), skip product preview (step 0)
    // On mobile, start at step 0 (product preview)
    // Check if we're on desktop by checking window width
    const isDesktop = window.innerWidth >= 1024; // lg breakpoint

    if (isDesktop) {
      // Desktop: step 2 for logged-in users, step 1 for guests
      setCurrentStep(user ? 2 : 1);
    } else {
      // Mobile: always start at step 0 (product preview)
      setCurrentStep(0);
    }

    if (!user) return;

    const primaryAddress = user.addresses?.find(addr => addr.address_type === 'residential')
      || user.addresses?.find(addr => addr.address_type === 'invoice')
      || user.addresses?.[0];

    // Convert country code to full country name
    const countryCode = primaryAddress?.country || '';
    const countryName = countries.find(c => c.code === countryCode)?.name || '';

    setFormData(prev => ({
      ...prev,
      firstName: user.firstname || '',
      lastName: user.lastname || '',
      email: user.email || '',
      confirmEmail: user.email || '',
      address: primaryAddress?.address_line1 || '',
      city: primaryAddress?.city || '',
      postalCode: primaryAddress?.postalcode || '',
      country: countryName,
    }));
  }, [isOpen, user, countries]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // Special handling for card number - format with spaces every 4 digits
    if (name === 'cardNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      const limitedDigits = digitsOnly.substring(0, 16);
      const formatted = limitedDigits.match(/.{1,4}/g)?.join(' ') || limitedDigits;
      setFormData(prev => ({
        ...prev,
        [name]: formatted,
      }));
      return;
    }

    // Special handling for expiry date - format as MM/YY
    if (name === 'expiryDate') {
      const digitsOnly = value.replace(/\D/g, '');
      const limitedDigits = digitsOnly.substring(0, 4);
      let formatted = limitedDigits;
      if (limitedDigits.length >= 2) {
        formatted = limitedDigits.substring(0, 2) + '/' + limitedDigits.substring(2);
      }
      setFormData(prev => ({
        ...prev,
        [name]: formatted,
      }));
      return;
    }

    // Special handling for CVV - only digits
    if (name === 'cvv') {
      const digitsOnly = value.replace(/\D/g, '');
      const limitedDigits = digitsOnly.substring(0, 4);
      setFormData(prev => ({
        ...prev,
        [name]: limitedDigits,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handlePurchase = async () => {
    if (!quoteData?.quoteId) return;
    setIsProcessing(true);
    try {
      const langCode = language === 'de' ? 'de-DE' : 'en-US';
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const provider = (useWalletBalance && quoteData.walletCanFullyCover)
        ? 'wallet' as const
        : paymentProviderMap[formData.paymentMethod];
      const countryCode = countries.find(c => c.name === formData.country)?.code || '';
      const invoiceAddress: CheckoutAddress = {
        address_type: 'invoice',
        street: formData.address,
        city: formData.city,
        state_province: '',
        postal_code: formData.postalCode,
        country: countryCode,
      };
      const result = await startCheckout({
        paymentProvider: provider,
        quoteId: quoteData.quoteId,
        customerEmail: formData.email,
        customerFirstname: formData.firstName,
        customerLastname: formData.lastName,
        isCompany: false,
        company: null,
        addresses: [invoiceAddress],
        languageCode: langCode,
        timezone: tz,
        receiveMarketingMails: false,
        userNotes: '',
        checkoutReferenceId: checkoutRefRef.current,
        ...(useWalletBalance ? { useWalletBalance: true } : {}),
      });
      if (result.isBlacklisted) {
        setIsProcessing(false);
        setQuoteData(prev => prev ? {
          ...prev,
          isBlacklisted: true,
          blacklistReason: result.blacklistReason ?? prev.blacklistReason,
        } : prev);
        return;
      }
      if (result.checkout_url) {
        window.open(result.checkout_url, '_blank');
        setIsProcessing(false);
        setCheckoutUrl(result.checkout_url);
        if (onClearCart) onClearCart();
        if (onPurchaseSuccess) onPurchaseSuccess();
      } else {
        setIsProcessing(false);
        setIsCompleted(true);
        if (onClearCart) onClearCart();
        if (onPurchaseSuccess) onPurchaseSuccess();
        setTimeout(() => {
          resetModal();
          onClose();
        }, 3000);
      }
    } catch (err: any) {
      setIsProcessing(false);
      const msg = err?.response?.data?.error || err?.message || 'Checkout failed';
      const isBlacklistError = /blacklist/i.test(msg);
      setQuoteError(isBlacklistError ? t.purchaseModal.blacklistedMessage : msg);
    }
  };

  const isStep1Valid = () => {
    return formData.firstName && formData.lastName && formData.email &&
           formData.country && formData.address && formData.city;
  };

  const isStep2Valid = () => {
    // All payment methods redirect to external providers, no additional fields required
    return true;
  };

  const isStep3Valid = () => {
    return formData.acceptTerms;
  };

  if (!isOpen || (cartItems.length === 0 && !checkoutUrl && !isCompleted)) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-2 md:p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={handleClose}
      />

      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl md:rounded-2xl w-full max-w-5xl max-h-[95vh] md:max-h-[90vh] overflow-hidden shadow-2xl border border-slate-700/50">
        {/* Ambient Background Effects */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-500/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 z-50 p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors backdrop-blur-sm"
        >
          <X className="w-5 h-5 text-slate-300 hover:text-white" />
        </button>

        {checkoutUrl ? (
          <div className="flex items-center justify-center min-h-[400px] md:min-h-[600px] p-6 md:p-12 relative">
            <div className="text-center relative z-10 max-w-md mx-auto">
              <div className="inline-block mb-6">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-full flex items-center justify-center border-2 border-amber-500/50 shadow-lg shadow-amber-500/20 animate-pulse">
                  <ExternalLink className="w-8 h-8 md:w-10 md:h-10 text-amber-400" />
                </div>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3">{t.purchaseModal.paymentRedirectTitle}</h3>
              <p className="text-sm md:text-base text-slate-300 mb-2 leading-relaxed">{t.purchaseModal.paymentRedirectMessage}</p>
              <p className="text-xs text-slate-500 mb-8">{t.purchaseModal.paymentRedirectCloseHint}</p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => window.open(checkoutUrl, '_blank')}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-slate-900 font-semibold py-3 rounded-xl transition-all shadow-lg hover:shadow-amber-500/25 active:scale-[.98] text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t.purchaseModal.paymentRedirectOpenAgain}
                </button>
                <button
                  onClick={() => { resetModal(); onClose(); }}
                  className="w-full inline-flex items-center justify-center gap-2 bg-slate-700/50 hover:bg-slate-700/80 border border-slate-600/50 hover:border-slate-500/50 text-white font-medium py-3 rounded-xl transition-all text-sm"
                >
                  <X className="w-4 h-4" />
                  {t.purchaseModal.paymentRedirectClose}
                </button>
              </div>
            </div>
          </div>
        ) : isBlacklistedForEval ? (
          <div className="flex items-center justify-center min-h-[400px] md:min-h-[600px] p-6 md:p-12 relative">
            <div className="text-center relative z-10 max-w-md mx-auto">
              <div className="inline-block mb-6">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-rose-500/20 to-red-500/20 rounded-full flex items-center justify-center border-2 border-rose-500/50 shadow-lg shadow-rose-500/20">
                  <AlertCircle className="w-8 h-8 md:w-10 md:h-10 text-rose-400" />
                </div>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3">{t.purchaseModal.blacklistedTitle}</h3>
              <p className="text-sm md:text-base text-slate-300 mb-8 leading-relaxed">{t.purchaseModal.blacklistedMessage}</p>
              <button
                onClick={handleClose}
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-700/50 hover:bg-slate-700/80 border border-slate-600/50 hover:border-slate-500/50 text-white font-medium py-3 rounded-xl transition-all text-sm"
              >
                <X className="w-4 h-4" />
                {t.purchaseModal.paymentRedirectClose}
              </button>
            </div>
          </div>
        ) : !isCompleted ? (
          <div className="flex flex-col lg:flex-row h-full relative max-h-[95vh] md:max-h-[90vh]">
            {/* Left Side - Product Card - Hidden on mobile */}
            <div className="hidden lg:flex lg:w-1/3 bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-6 flex-col backdrop-blur-sm">
              {/* Logo */}
              <div className="mb-4">
                <img
                  src="/images/Nextproptrader Logo W.png"
                  alt="Nextproptrader"
                  className="h-8 w-auto object-contain"
                />
              </div>

              <div className="border-t border-slate-700/50 mb-4"></div>

              {/* Product Card or Cart List */}
              <div className="flex-1 flex items-start justify-center overflow-y-auto">
                {shouldShowCartList ? (
                  <div className="w-full max-w-sm">
                    <CartList
                      items={cartItems}
                      onRemoveItem={onRemoveItem || (() => {})}
                      onUpdateQuantity={onUpdateQuantity}
                      tradingSystem={formData.tradingSystem}
                      onTradingSystemChange={(system) => setFormData(prev => ({ ...prev, tradingSystem: system }))}
                      selectedCurrency={selectedCurrency}
                    />
                  </div>
                ) : (
                  <div className="bg-slate-800/80 backdrop-blur-md rounded-3xl p-5 shadow-lg w-full max-w-sm flex flex-col hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-500 border border-slate-700/50">
                    {/* Product Image with Animated Gradients */}
                    <div className="relative rounded-2xl overflow-hidden mb-4 aspect-[4/3] flex items-center justify-center group cursor-pointer">
                      {/* Animated Background Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
                        {/* Animated Gradient Orbs */}
                        <div className="absolute inset-0">
                          {/* Yellow/Amber Orb */}
                          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-br from-yellow-500/30 via-amber-500/20 to-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
                          {/* Blue Orb */}
                          <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-gradient-to-br from-blue-500/20 via-cyan-500/15 to-slate-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                          {/* Orange Accent */}
                          <div className="absolute top-1/2 right-1/3 w-24 h-24 bg-gradient-to-br from-orange-500/25 via-amber-500/15 to-yellow-500/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                        </div>

                        {/* Animated Grid Pattern */}
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(251,191,36,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(251,191,36,0.03)_1px,transparent_1px)] bg-[size:20px_20px] opacity-50"></div>

                        {/* Shimmer Effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      </div>

                      {/* Content */}
                      <div className="relative z-10 text-center transform group-hover:scale-105 transition-transform duration-500">
                        <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white via-amber-100 to-yellow-200 mb-2 tracking-tight drop-shadow-lg">
                          {isFundedFee ? 'FUNDED' : singleItem?.type === 'Reset' ? 'RESET' : isExtensionType(singleItem?.type) ? 'Extension' : (productDetails?.size || displayProduct?.size || '$50K')}
                        </div>
                        <div className="text-xs font-medium text-amber-200/90 uppercase tracking-widest drop-shadow">
                          {isFundedFee ? 'ACTIVATION' : singleItem?.type === 'Reset' ? 'Account' : isExtensionType(singleItem?.type) ? (singleItem?.accountType === 'Funded' ? 'Funded' : 'Evaluation') : t.purchaseModal.challenge}
                        </div>

                        {/* Floating Particles Effect */}
                        <div className="absolute inset-0 pointer-events-none">
                          <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-yellow-400 rounded-full animate-ping opacity-75"></div>
                          <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-amber-400 rounded-full animate-ping opacity-75" style={{ animationDelay: '0.5s' }}></div>
                          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-orange-400 rounded-full animate-ping opacity-75" style={{ animationDelay: '1s' }}></div>
                        </div>
                      </div>

                      {/* Discount Badge */}
                      {discountPercentage > 0 && (
                        <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg shadow-amber-500/30">
                          -{discountPercentage}%
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="mb-3">
                      {(displayProduct?.subtitle || singleItem?.type === 'Reset' || isExtensionType(singleItem?.type) || isFundedFee) && (
                        <div className="text-xs text-slate-400 mb-1 font-medium">
                          {isFundedFee ? 'Funded Fee' : (singleItem?.type === 'Reset' || isExtensionType(singleItem?.type)) ? singleItem?.accountNumber : 'New Evaluation'}
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <h3 className="text-lg font-bold text-white leading-tight">
                          {displayProduct?.name}
                        </h3>
                        <div className="text-right">
                          {originalPrice && (
                            <div className="text-sm font-medium text-slate-500 line-through mb-0.5">
                              {currencySymbol}{formatPrice(originalPrice)}
                            </div>
                          )}
                          <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-br from-amber-400 to-yellow-500 whitespace-nowrap">
                            {currencySymbol}{formatPrice(getTotal())}
                          </div>
                        </div>
                      </div>

                      {activeVolumeDiscount && singleItem && (
                        <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5 mb-3">
                          <Tag className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                          <span className="text-[10px] text-emerald-300 font-medium">
                            {(t.purchaseModal.volumeDiscountApplied || 'Volume discount applied ({percentage}% off)')
                              .replace('{percentage}', String(discountPercentage))}
                            {' '}&middot;{' '}
                            {(t.purchaseModal.volumeDiscountSavings || 'You save {symbol}{amount} per unit')
                              .replace('{symbol}', currencySymbol)
                              .replace('{amount}', discountAmount.toFixed(2))}
                          </span>
                        </div>
                      )}

                      {/* Product Description with Features */}
                      <div className="text-sm text-slate-300 leading-relaxed space-y-1">
                        {(singleItem?.type === 'Reset' || isExtensionType(singleItem?.type) || isFundedFee) ? (
                          <p className="text-slate-300">{isFundedFee ? t.purchaseModal.fundedFeeDescription : singleItem?.type === 'Reset' ? t.purchaseModal.resetDescription : t.purchaseModal.extensionDescription}</p>
                        ) : (
                          <>
                            {productDetails?.minTradingDays && (
                              <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                <span className="flex-1">{t.purchaseModal.minTradingDays || 'Min. Trading Days'}: <strong className="text-white">{productDetails.minTradingDays}</strong></span>
                              </div>
                            )}
                            {productDetails?.profitTarget && (
                              <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                <span className="flex-1">{t.purchaseModal.profitTarget}: <strong className="text-white">{productDetails.profitTarget}</strong></span>
                              </div>
                            )}
                            {productDetails?.maxLoss && (
                              <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                <span className="flex-1">{t.purchaseModal.maxLoss || 'Max Loss'}: <strong className="text-white">{productDetails.maxLoss}</strong></span>
                              </div>
                            )}
                            {productDetails?.profitSplit && (
                              <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                <span className="flex-1">{t.purchaseModal.profitSplit}: <strong className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">{productDetails.profitSplit}</strong></span>
                              </div>
                            )}
                            {productDetails?.contractLimits && (
                              <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                <span className="flex-1">{t.purchaseModal.maxLots || 'Max Contracts'}: <strong className="text-white">{productDetails.contractLimits.minis} Minis / {productDetails.contractLimits.micros} Micros</strong></span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Trading System Selection - Only for Account type */}
                    {singleItem?.type === 'Account' && (
                      <div className="mt-4 pt-4 border-t border-slate-700/50">
                        <label className={`block text-[10px] font-medium mb-2 uppercase tracking-wider ${
                          !formData.tradingSystem ? 'text-amber-400' : 'text-slate-400'
                        }`}>
                          {t.purchaseModal.tradingSystem || 'Trading System'}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {/* Rithmic Option */}
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, tradingSystem: 'rithmic' }))}
                            className={`relative p-2.5 rounded-lg border transition-all duration-200 flex flex-col items-center justify-center gap-1.5 ${
                              formData.tradingSystem === 'rithmic'
                                ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/20'
                                : !formData.tradingSystem
                                  ? 'border-amber-500/40 bg-slate-800/50 hover:border-amber-500 hover:bg-slate-800/70 animate-pulse'
                                  : 'border-slate-600/50 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800/70'
                            }`}
                          >
                            {formData.tradingSystem === 'rithmic' && (
                              <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center">
                                <Check className="w-2 h-2 text-slate-900" />
                              </div>
                            )}
                            <img
                              src="/images/datafeeds/rithmic logo.png"
                              alt="Rithmic"
                              className="h-5 w-auto object-contain"
                            />
                            <span className={`text-[10px] font-medium ${
                              formData.tradingSystem === 'rithmic' ? 'text-amber-400' : 'text-slate-300'
                            }`}>
                              Rithmic
                            </span>
                          </button>

                          {/* DXFeed Option */}
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, tradingSystem: 'dxfeed' }))}
                            className={`relative p-2.5 rounded-lg border transition-all duration-200 flex flex-col items-center justify-center gap-1.5 ${
                              formData.tradingSystem === 'dxfeed'
                                ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/20'
                                : !formData.tradingSystem
                                  ? 'border-amber-500/40 bg-slate-800/50 hover:border-amber-500 hover:bg-slate-800/70 animate-pulse'
                                  : 'border-slate-600/50 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800/70'
                            }`}
                          >
                            {formData.tradingSystem === 'dxfeed' && (
                              <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center">
                                <Check className="w-2 h-2 text-slate-900" />
                              </div>
                            )}
                            <img
                              src="/images/datafeeds/dxfeed logo.png"
                              alt="DXFeed"
                              className="h-5 w-auto object-contain"
                            />
                            <span className={`text-[10px] font-medium ${
                              formData.tradingSystem === 'dxfeed' ? 'text-amber-400' : 'text-slate-300'
                            }`}>
                              DXFeed
                            </span>
                          </button>
                        </div>
                        {!formData.tradingSystem && (
                          <p className="text-[10px] text-amber-400 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {t.purchaseModal.selectTradingSystem || 'Please select a data feed'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="w-full lg:w-2/3 bg-slate-900/50 backdrop-blur-sm p-4 md:p-6 lg:p-8 overflow-y-auto lg:border-l border-slate-700/30">
              {/* Logo - Mobile Only */}
              <div className="lg:hidden mb-6 text-center">
                <img
                  src="/images/Nextproptrader Logo W.png"
                  alt="Nextproptrader"
                  className="h-6 w-auto object-contain mx-auto"
                />
              </div>

              {/* Minimalist Step Indicator - Hide on mobile when in step 0 */}
              <div className={`mb-6 md:mb-10 max-w-3xl mx-auto ${currentStep === 0 ? 'hidden lg:flex' : 'flex'}`}>
                <div className="flex items-center justify-center w-full">
                  {/* Step 1 */}
                  <button
                    onClick={() => !user && currentStep !== 0 && setCurrentStep(1)}
                    disabled={!!user || currentStep === 0}
                    className={`flex items-center gap-1 md:gap-2 group ${user || currentStep === 0 ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                  >
                    <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      currentStep >= 1 || user
                        ? 'bg-amber-500 text-slate-900'
                        : 'bg-slate-800 text-slate-500 border border-slate-700'
                    }`}>
                      {currentStep > 1 || user ? <Check className="w-3 h-3 md:w-4 md:h-4" /> : '1'}
                    </div>
                    <p className={`text-[10px] md:text-xs uppercase tracking-wider font-medium transition-colors hidden sm:block ${
                      currentStep === 1 && !user ? 'text-amber-400' : 'text-slate-400'
                    }`}>
                      {t.purchaseModal.personal}
                    </p>
                  </button>

                  {/* Connector Line 1-2 */}
                  <div className="mx-2 md:mx-4 w-8 md:w-12 h-[1px] bg-slate-800 relative overflow-hidden">
                    <div
                      className="absolute inset-0 bg-amber-500 transition-transform duration-500 origin-left"
                      style={{
                        transform: currentStep >= 2 ? 'scaleX(1)' : 'scaleX(0)'
                      }}
                    />
                  </div>

                  {/* Step 2 */}
                  <button
                    onClick={() => isStep1Valid() && setCurrentStep(2)}
                    disabled={!isStep1Valid()}
                    className="flex items-center gap-1 md:gap-2 group disabled:cursor-not-allowed"
                  >
                    <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      currentStep >= 2
                        ? 'bg-amber-500 text-slate-900'
                        : 'bg-slate-800 text-slate-500 border border-slate-700'
                    } ${!isStep1Valid() ? 'opacity-40' : ''}`}>
                      {currentStep > 2 ? <Check className="w-3 h-3 md:w-4 md:h-4" /> : '2'}
                    </div>
                    <p className={`text-[10px] md:text-xs uppercase tracking-wider font-medium transition-colors hidden sm:block ${
                      currentStep === 2 ? 'text-amber-400' : currentStep > 2 ? 'text-slate-400' : 'text-slate-600'
                    } ${!isStep1Valid() ? 'opacity-40' : ''}`}>
                      {t.purchaseModal.payment}
                    </p>
                  </button>

                  {/* Connector Line 2-3 */}
                  <div className="mx-2 md:mx-4 w-8 md:w-12 h-[1px] bg-slate-800 relative overflow-hidden">
                    <div
                      className="absolute inset-0 bg-amber-500 transition-transform duration-500 origin-left"
                      style={{
                        transform: currentStep === 3 ? 'scaleX(1)' : 'scaleX(0)'
                      }}
                    />
                  </div>

                  {/* Step 3 */}
                  <button
                    onClick={() => isStep1Valid() && isStep2Valid() && setCurrentStep(3)}
                    disabled={!isStep1Valid() || !isStep2Valid()}
                    className="flex items-center gap-1 md:gap-2 group disabled:cursor-not-allowed"
                  >
                    <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      currentStep === 3
                        ? 'bg-amber-500 text-slate-900'
                        : 'bg-slate-800 text-slate-500 border border-slate-700'
                    } ${!isStep1Valid() || !isStep2Valid() ? 'opacity-40' : ''}`}>
                      3
                    </div>
                    <p className={`text-[10px] md:text-xs uppercase tracking-wider font-medium transition-colors hidden sm:block ${
                      currentStep === 3 ? 'text-amber-400' : 'text-slate-600'
                    } ${!isStep1Valid() || !isStep2Valid() ? 'opacity-40' : ''}`}>
                      {t.purchaseModal.review}
                    </p>
                  </button>
                </div>
              </div>

              {/* Step 0: Product Preview - Mobile Only */}
              {currentStep === 0 && (
                <div className="space-y-6 lg:hidden">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4 text-center">
                    {shouldShowCartList ? t.purchaseModal.yourCart || 'Your Cart' : t.purchaseModal.productDetails || 'Product Details'}
                  </h2>

                  {/* Product Card or Cart List */}
                  <div className="w-full max-w-md mx-auto">
                    {shouldShowCartList ? (
                      <CartList
                        items={cartItems}
                        onRemoveItem={onRemoveItem || (() => {})}
                        onUpdateQuantity={onUpdateQuantity}
                        tradingSystem={formData.tradingSystem}
                        onTradingSystemChange={(system) => setFormData(prev => ({ ...prev, tradingSystem: system }))}
                      />
                    ) : (
                      <div className="bg-slate-800/80 backdrop-blur-md rounded-3xl p-5 shadow-lg flex flex-col hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-500 border border-slate-700/50">
                        {/* Product Image with Animated Gradients */}
                        <div className="relative rounded-2xl overflow-hidden mb-4 aspect-[4/3] flex items-center justify-center group cursor-pointer">
                          {/* Animated Background Gradient */}
                          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
                            {/* Animated Gradient Orbs */}
                            <div className="absolute inset-0">
                              {/* Yellow/Amber Orb */}
                              <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-br from-yellow-500/30 via-amber-500/20 to-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
                              {/* Blue Orb */}
                              <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-gradient-to-br from-blue-500/20 via-cyan-500/15 to-slate-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                              {/* Orange Accent */}
                              <div className="absolute top-1/2 right-1/3 w-24 h-24 bg-gradient-to-br from-orange-500/25 via-amber-500/15 to-yellow-500/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                            </div>

                            {/* Animated Grid Pattern */}
                            <div className="absolute inset-0 bg-[linear-gradient(rgba(251,191,36,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(251,191,36,0.03)_1px,transparent_1px)] bg-[size:20px_20px] opacity-50"></div>

                            {/* Shimmer Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                          </div>

                          {/* Content */}
                          <div className="relative z-10 text-center transform group-hover:scale-105 transition-transform duration-500">
                            <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white via-amber-100 to-yellow-200 mb-2 tracking-tight drop-shadow-lg">
                              {isFundedFee ? 'FUNDED' : singleItem?.type === 'Reset' ? 'RESET' : isExtensionType(singleItem?.type) ? 'Extension' : (productDetails?.size || displayProduct?.size || '$50K')}
                            </div>
                            <div className="text-xs font-medium text-amber-200/90 uppercase tracking-widest drop-shadow">
                              {isFundedFee ? 'ACTIVATION' : singleItem?.type === 'Reset' ? 'Account' : isExtensionType(singleItem?.type) ? (singleItem?.accountType === 'Funded' ? 'Funded' : 'Evaluation') : t.purchaseModal.challenge}
                            </div>

                            {/* Floating Particles Effect */}
                            <div className="absolute inset-0 pointer-events-none">
                              <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-yellow-400 rounded-full animate-ping opacity-75"></div>
                              <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-amber-400 rounded-full animate-ping opacity-75" style={{ animationDelay: '0.5s' }}></div>
                              <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-orange-400 rounded-full animate-ping opacity-75" style={{ animationDelay: '1s' }}></div>
                            </div>
                          </div>

                          {/* Discount Badge */}
                          {discountPercentage > 0 && (
                            <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg shadow-amber-500/30">
                              -{discountPercentage}%
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="mb-3">
                          {(displayProduct?.subtitle || singleItem?.type === 'Reset' || isExtensionType(singleItem?.type) || isFundedFee) && (
                            <div className="text-xs text-slate-400 mb-1 font-medium">
                              {isFundedFee ? 'Funded Fee' : (singleItem?.type === 'Reset' || isExtensionType(singleItem?.type)) ? singleItem?.accountNumber : 'New Evaluation'}
                            </div>
                          )}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <h3 className="text-lg font-bold text-white leading-tight">
                              {displayProduct?.name}
                            </h3>
                            <div className="text-right">
                              {originalPrice && (
                                <div className="text-sm font-medium text-slate-500 line-through mb-0.5">
                                  {currencySymbol}{formatPrice(originalPrice)}
                                </div>
                              )}
                              <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-br from-amber-400 to-yellow-500 whitespace-nowrap">
                                {currencySymbol}{formatPrice(getTotal())}
                              </div>
                            </div>
                          </div>

                          {activeVolumeDiscount && singleItem && (
                            <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5 mb-3">
                              <Tag className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                              <span className="text-[10px] text-emerald-300 font-medium">
                                {(t.purchaseModal.volumeDiscountApplied || 'Volume discount applied ({percentage}% off)')
                                  .replace('{percentage}', String(discountPercentage))}
                                {' '}&middot;{' '}
                                {(t.purchaseModal.volumeDiscountSavings || 'You save {symbol}{amount} per unit')
                                  .replace('{symbol}', currencySymbol)
                                  .replace('{amount}', discountAmount.toFixed(2))}
                              </span>
                            </div>
                          )}

                          {/* Product Description with Features */}
                          <div className="text-sm text-slate-300 leading-relaxed space-y-1">
                            {(singleItem?.type === 'Reset' || isExtensionType(singleItem?.type) || isFundedFee) ? (
                              <p className="text-slate-300">{isFundedFee ? t.purchaseModal.fundedFeeDescription : singleItem?.type === 'Reset' ? t.purchaseModal.resetDescription : t.purchaseModal.extensionDescription}</p>
                            ) : (
                              <>
                                {productDetails?.minTradingDays && (
                                  <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                    <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                    <span className="flex-1">{t.purchaseModal.minTradingDays || 'Min. Trading Days'}: <strong className="text-white">{productDetails.minTradingDays}</strong></span>
                                  </div>
                                )}
                                {productDetails?.profitTarget && (
                                  <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                    <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                    <span className="flex-1">{t.purchaseModal.profitTarget}: <strong className="text-white">{productDetails.profitTarget}</strong></span>
                                  </div>
                                )}
                                {productDetails?.maxLoss && (
                                  <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                    <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                    <span className="flex-1">{t.purchaseModal.maxLoss || 'Max Loss'}: <strong className="text-white">{productDetails.maxLoss}</strong></span>
                                  </div>
                                )}
                                {productDetails?.profitSplit && (
                                  <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                    <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                    <span className="flex-1">{t.purchaseModal.profitSplit}: <strong className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">{productDetails.profitSplit}</strong></span>
                                  </div>
                                )}
                                {productDetails?.contractLimits && (
                                  <div className="flex items-start gap-2 group/feature hover:translate-x-1 transition-transform duration-200">
                                    <span className="w-1 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mt-2 flex-shrink-0 group-hover/feature:shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
                                    <span className="flex-1">{t.purchaseModal.maxLots || 'Max Contracts'}: <strong className="text-white">{productDetails.contractLimits.minis} Minis / {productDetails.contractLimits.micros} Micros</strong></span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Trading System Selection - Only for Account type */}
                        {singleItem?.type === 'Account' && (
                          <div className="mt-4 pt-4 border-t border-slate-700/50">
                            <label className={`block text-[10px] font-medium mb-2 uppercase tracking-wider ${
                              !formData.tradingSystem ? 'text-amber-400' : 'text-slate-400'
                            }`}>
                              {t.purchaseModal.tradingSystem || 'Trading System'}
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {/* Rithmic Option */}
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, tradingSystem: 'rithmic' }))}
                                className={`relative p-2.5 rounded-lg border transition-all duration-200 flex flex-col items-center justify-center gap-1.5 ${
                                  formData.tradingSystem === 'rithmic'
                                    ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/20'
                                    : !formData.tradingSystem
                                      ? 'border-amber-500/40 bg-slate-800/50 hover:border-amber-500 hover:bg-slate-800/70 animate-pulse'
                                      : 'border-slate-600/50 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800/70'
                                }`}
                              >
                                {formData.tradingSystem === 'rithmic' && (
                                  <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center">
                                    <Check className="w-2 h-2 text-slate-900" />
                                  </div>
                                )}
                                <img
                                  src="/images/datafeeds/rithmic logo.png"
                                  alt="Rithmic"
                                  className="h-5 w-auto object-contain"
                                />
                                <span className={`text-[10px] font-medium ${
                                  formData.tradingSystem === 'rithmic' ? 'text-amber-400' : 'text-slate-300'
                                }`}>
                                  Rithmic
                                </span>
                              </button>

                              {/* DXFeed Option */}
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, tradingSystem: 'dxfeed' }))}
                                className={`relative p-2.5 rounded-lg border transition-all duration-200 flex flex-col items-center justify-center gap-1.5 ${
                                  formData.tradingSystem === 'dxfeed'
                                    ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/20'
                                    : !formData.tradingSystem
                                      ? 'border-amber-500/40 bg-slate-800/50 hover:border-amber-500 hover:bg-slate-800/70 animate-pulse'
                                      : 'border-slate-600/50 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800/70'
                                }`}
                              >
                                {formData.tradingSystem === 'dxfeed' && (
                                  <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center">
                                    <Check className="w-2 h-2 text-slate-900" />
                                  </div>
                                )}
                                <img
                                  src="/images/datafeeds/dxfeed logo.png"
                                  alt="DXFeed"
                                  className="h-5 w-auto object-contain"
                                />
                                <span className={`text-[10px] font-medium ${
                                  formData.tradingSystem === 'dxfeed' ? 'text-amber-400' : 'text-slate-300'
                                }`}>
                                  DXFeed
                                </span>
                              </button>
                            </div>
                            {!formData.tradingSystem && (
                              <p className="text-[10px] text-amber-400 mt-2 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {t.purchaseModal.selectTradingSystem || 'Please select a data feed'}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Continue Button - Mobile Only */}
                  <button
                    onClick={() => setCurrentStep(user ? 2 : 1)}
                    disabled={isTradingSystemRequired}
                    className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-slate-900 font-medium py-3 rounded-lg transition-all shadow-lg mt-6 text-sm active:scale-[.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{user ? t.purchaseModal.continueToPayment : t.purchaseModal.continue || 'Continue'}</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4">{t.purchaseModal.personalInformation}</h2>

                  {/* Name Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        {t.purchaseModal.firstName}
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-white placeholder:text-slate-400"
                        placeholder={t.purchaseModal.enterFirstName}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        {t.purchaseModal.lastName}
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-white placeholder:text-slate-400"
                        placeholder={t.purchaseModal.enterLastName}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      {t.purchaseModal.emailAddress}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-white placeholder:text-slate-400"
                      placeholder={t.purchaseModal.enterEmail}
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      {t.purchaseModal.countryOrRegion}
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-white text-left flex items-center justify-between"
                        onClick={() => setIsCountryOpen(v => !v)}
                      >
                        <span className={formData.country ? 'text-white' : 'text-slate-400'}>
                          {formData.country || t.purchaseModal.selectCountry}
                        </span>
                        <svg className={`w-5 h-5 text-slate-400 transition-transform ${isCountryOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isCountryOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700/95 backdrop-blur-xl border border-slate-600/50 rounded-lg shadow-2xl z-50 overflow-hidden">
                          <div className="p-2 border-b border-slate-600/30">
                            <input
                              type="text"
                              placeholder={t.purchaseModal.searchCountries}
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              className="w-full bg-slate-600/50 border border-slate-500/50 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-amber-500/70 text-xs"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {filteredCountries.length ? (
                              filteredCountries.map((country) => (
                                <div
                                  key={country.code}
                                  onClick={() => {
                                    const fakeEvent = { target: { name: 'country', value: country.name, type: 'text' } } as unknown as React.ChangeEvent<HTMLInputElement>;
                                    handleInputChange(fakeEvent);
                                    setIsCountryOpen(false);
                                    setCountrySearch('');
                                  }}
                                  className="px-3 py-2 text-white hover:bg-slate-600/50 cursor-pointer transition-colors border-b border-slate-600/20 last:border-b-0 text-sm"
                                >
                                  {country.name}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-slate-400 text-center text-sm">{t.purchaseModal.noCountriesFound}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      {t.purchaseModal.address}
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-white placeholder:text-slate-400"
                      placeholder={t.purchaseModal.enterStreetAddress}
                    />
                  </div>

                  {/* City & Postal Code */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        {t.purchaseModal.city}
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-white placeholder:text-slate-400"
                        placeholder={t.purchaseModal.enterCity}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        {t.purchaseModal.postalCode}
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-white placeholder:text-slate-400"
                        placeholder={t.purchaseModal.enterPostalCode}
                      />
                    </div>
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!isStep1Valid()}
                    className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-slate-900 font-medium py-2.5 md:py-3 rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed mt-4 md:mt-6 text-sm active:scale-[.98] flex items-center justify-center gap-2"
                  >
                    <span>{t.purchaseModal.continueToPayment}</span>
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </div>
              )}

              {/* Step 2: Payment Information */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4">{t.purchaseModal.paymentDetails}</h2>

                  {/* Payment Method Tabs */}
                  <div className="grid grid-cols-3 gap-2 md:flex md:gap-3 mb-4">
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'card' }))}
                      className={`group flex-1 flex flex-col items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-3 md:py-4 rounded-xl border-2 transition-all duration-300 ${
                        formData.paymentMethod === 'card'
                          ? 'border-amber-500 bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-amber-600/20 text-white shadow-xl shadow-amber-500/20 scale-105'
                          : 'border-slate-600/50 bg-slate-800/40 backdrop-blur-sm text-slate-300 hover:border-amber-500/50 hover:bg-slate-700/40 hover:text-white hover:scale-102'
                      }`}
                    >
                      <CreditCard className={`w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 ${
                        formData.paymentMethod === 'card' ? 'text-amber-400' : 'text-slate-400 group-hover:text-amber-400 group-hover:scale-110'
                      }`} />
                      <span className="font-semibold text-[10px] md:text-xs">{t.purchaseModal.card}</span>
                    </button>

                    <button
                      onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'paypal' }))}
                      className={`group flex-1 flex flex-col items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-3 md:py-4 rounded-xl border-2 transition-all duration-300 ${
                        formData.paymentMethod === 'paypal'
                          ? 'border-amber-500 bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-amber-600/20 text-white shadow-xl shadow-amber-500/20 scale-105'
                          : 'border-slate-600/50 bg-slate-800/40 backdrop-blur-sm text-slate-300 hover:border-amber-500/50 hover:bg-slate-700/40 hover:text-white hover:scale-102'
                      }`}
                    >
                      <Wallet className={`w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 ${
                        formData.paymentMethod === 'paypal' ? 'text-amber-400' : 'text-slate-400 group-hover:text-amber-400 group-hover:scale-110'
                      }`} />
                      <span className="font-semibold text-[10px] md:text-xs">{t.purchaseModal.paypal}</span>
                    </button>

                    <div
                      className="group relative flex-1 flex flex-col items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-3 md:py-4 rounded-xl border-2 border-slate-700/50 bg-slate-800/20 text-slate-500 cursor-not-allowed opacity-60"
                    >
                      <div className="absolute -top-2.5 right-1.5 flex items-center gap-1 px-1.5 py-0.5 bg-slate-700 border border-slate-600/50 rounded-full">
                        <Clock className="w-2.5 h-2.5 text-amber-400" />
                        <span className="text-[8px] font-semibold text-amber-400 uppercase tracking-wider">{t.purchaseModal.soon}</span>
                      </div>
                      <Bitcoin className="w-5 h-5 md:w-6 md:h-6 text-slate-600" />
                      <span className="font-semibold text-[10px] md:text-xs text-slate-500">{t.purchaseModal.crypto}</span>
                    </div>
                  </div>

                  {/* Card Payment - Stripe */}
                  {formData.paymentMethod === 'card' && (
                    <div className="space-y-4">
                      <div className="bg-slate-800/30 border border-slate-600/50 rounded-lg p-6 flex flex-col items-center justify-center text-center">
                        <img
                          src="/images/payments/stripe_logo.png"
                          alt="Stripe"
                          className="h-12 mb-4 object-contain"
                        />
                        <p className="text-slate-300 text-sm leading-relaxed">
                          {t.purchaseModal.stripeRedirectDescription}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* PayPal Payment */}
                  {formData.paymentMethod === 'paypal' && (
                    <div className="space-y-4">
                      <div className="bg-slate-800/30 border border-slate-600/50 rounded-lg p-6 flex flex-col items-center justify-center text-center">
                        <img
                          src="/images/payments/paypal_logo.png"
                          alt="PayPal"
                          className="h-12 mb-4 object-contain"
                        />
                        <p className="text-slate-300 text-sm leading-relaxed">
                          {t.purchaseModal.paypalRedirectDescription}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Crypto Payment - Coming Soon */}

                  {/* Currency Selection */}
                  <div className="mt-4">
                    <label className="block text-[10px] font-medium text-slate-400 mb-2 uppercase tracking-wider">
                      {t.purchaseModal.currency}
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedCurrency('EUR')}
                        className={`flex-1 px-3 py-2 rounded-lg border transition-all duration-200 flex items-center justify-center gap-1.5 text-xs font-medium ${
                          selectedCurrency === 'EUR'
                            ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                            : 'border-slate-600/50 bg-slate-800/50 text-slate-300 hover:border-slate-500'
                        }`}
                      >
                        <span>€</span>
                        <span>EUR</span>
                      </button>
                      {hasUsdPrices && (
                        <button
                          type="button"
                          onClick={() => setSelectedCurrency('USD')}
                          className={`flex-1 px-3 py-2 rounded-lg border transition-all duration-200 flex items-center justify-center gap-1.5 text-xs font-medium ${
                            selectedCurrency === 'USD'
                              ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                              : 'border-slate-600/50 bg-slate-800/50 text-slate-300 hover:border-slate-500'
                          }`}
                        >
                          <span>$</span>
                          <span>USD</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Wallet Balance Option */}
                  {user && walletAvailableForCurrency > 0 && (
                    <div className="mt-4">
                      <label className="flex items-start gap-3 cursor-pointer group p-3.5 rounded-xl border transition-all duration-200 bg-slate-800/30 hover:bg-slate-800/50 border-slate-700/40 hover:border-amber-500/30">
                        <input
                          type="checkbox"
                          checked={useWalletBalance}
                          onChange={(e) => setUseWalletBalance(e.target.checked)}
                          className="mt-0.5 w-4 h-4 rounded border-slate-600 bg-slate-800/50 text-amber-500 focus:ring-2 focus:ring-amber-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Wallet className="w-4 h-4 text-amber-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-white group-hover:text-amber-50 transition-colors">
                              {t.purchaseModal.useWalletBalance}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {(t.purchaseModal.walletBalanceAvailable || 'Available: {symbol}{amount}')
                              .replace('{symbol}', currencySymbol)
                              .replace('{amount}', walletAvailableForCurrency.toFixed(2))}
                          </p>
                        </div>
                      </label>
                    </div>
                  )}

                  {/* Continue to Review Button */}
                  <button
                    onClick={async () => {
                      setCurrentStep(3);
                      await requestQuote(appliedCoupon);
                    }}
                    disabled={!isStep2Valid() || quoteLoading || isTradingSystemRequired}
                    className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-slate-900 font-medium py-2.5 md:py-3 rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed mt-4 md:mt-6 text-sm active:scale-[.98] flex items-center justify-center gap-2"
                  >
                    {quoteLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <span>{t.purchaseModal.continueToReview}</span>
                        <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Step 3: Review & Confirm */}
              {currentStep === 3 && (
                <div className="space-y-4 md:space-y-6">
                  <h2 className="text-lg md:text-xl font-bold text-white mb-4">{t.purchaseModal.reviewAndConfirm}</h2>

                  {quoteLoading && (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                      <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
                      <p className="text-sm text-slate-400">{t.purchaseModal.loadingQuote || 'Loading quote...'}</p>
                    </div>
                  )}

                  {quoteError && !quoteLoading && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-red-300">{quoteError}</p>
                        <button
                          onClick={() => requestQuote(appliedCoupon)}
                          className="mt-2 text-xs text-amber-400 hover:text-amber-300 font-medium underline"
                        >
                          {t.purchaseModal.retry || 'Retry'}
                        </button>
                      </div>
                    </div>
                  )}

                  {!quoteLoading && quoteData && quoteData.isBlacklisted && (
                    <div className="flex flex-col items-center justify-center py-8 md:py-12">
                      <div className="inline-block mb-6">
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-rose-500/20 to-red-500/20 rounded-full flex items-center justify-center border-2 border-rose-500/50 shadow-lg shadow-rose-500/20">
                          <AlertCircle className="w-8 h-8 md:w-10 md:h-10 text-rose-400" />
                        </div>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-white mb-3 text-center">{t.purchaseModal.blacklistedTitle}</h3>
                      {quoteData.blacklistReason ? (
                        <p className="text-sm text-slate-300 text-center mb-2 leading-relaxed max-w-md">{quoteData.blacklistReason}</p>
                      ) : (
                        <p className="text-sm text-slate-300 text-center mb-2 leading-relaxed max-w-md">{t.purchaseModal.blacklistedMessage}</p>
                      )}
                    </div>
                  )}

                  {!quoteLoading && quoteData && !quoteData.isBlacklisted && (
                    <>
                      {/* Mobile Product Summary */}
                      <div className="lg:hidden bg-slate-800/30 border border-slate-600/50 rounded-lg p-4 mb-4">
                        <div className="space-y-2">
                          {quoteData.items.map((qItem, idx) => {
                            const cartItem = cartItems[idx];
                            return (
                              <div key={`${qItem.productId}-${idx}`} className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-white">{cartItem?.name || qItem.productId}</p>
                                  <p className="text-xs text-slate-400">{t.purchaseModal.quantity}: {qItem.quantity}</p>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-br from-amber-400 to-yellow-500">
                                    {currencySymbol}{qItem.totalAmount}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Coupon Code Section */}
                      <div className="bg-slate-800/30 border border-slate-600/50 rounded-lg p-4">
                        <label className="block text-xs font-medium text-slate-300 mb-2">
                          {t.purchaseModal.couponCode}
                        </label>
                        {!appliedCoupon ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              placeholder={t.purchaseModal.enterCouponCode}
                              className="flex-1 px-3 py-2.5 bg-slate-800/50 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-white placeholder:text-slate-400"
                            />
                            <button
                              onClick={handleApplyCoupon}
                              disabled={quoteLoading || !couponCode.trim()}
                              className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t.purchaseModal.applyCoupon}
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between bg-slate-800/50 border border-green-500/30 rounded-lg px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-400" />
                              <span className="text-sm text-green-300 font-medium">{appliedCoupon}</span>
                              {quoteData.discountAmountMinor > 0 && (
                                <span className="text-xs text-green-400/70">
                                  (-{currencySymbol}{(quoteData.discountAmountMinor / 100).toFixed(2)})
                                </span>
                              )}
                            </div>
                            <button
                              onClick={handleRemoveCoupon}
                              className="text-xs text-slate-400 hover:text-red-400 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Order Summary */}
                      <div className="bg-slate-800/30 border border-slate-600/50 rounded-lg p-5">
                        <h3 className="text-base font-semibold text-white mb-4">{t.purchaseModal.orderSummary}</h3>

                        <div className="space-y-3 mb-4 pb-4 border-b border-slate-600/50">
                          {quoteData.items.map((qItem, idx) => {
                            const cartItem = cartItems[idx];
                            return (
                              <div key={`${qItem.productId}-${idx}`} className="flex justify-between items-center">
                                <div className="flex-1">
                                  <p className="text-sm text-white">{cartItem?.name || qItem.productId}</p>
                                  <p className="text-xs text-slate-400">
                                    {t.purchaseModal.quantity}: {qItem.quantity}
                                    {qItem.unitAmount !== qItem.totalAmount && (
                                      <span className="ml-2">@ {currencySymbol}{qItem.unitAmount}</span>
                                    )}
                                  </p>
                                </div>
                                <p className="text-sm font-medium text-white">
                                  {currencySymbol}{qItem.totalAmount}
                                </p>
                              </div>
                            );
                          })}
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">{t.purchaseModal.subtotal}</span>
                            <span className="text-white font-medium">{currencySymbol}{quoteData.totalAmount}</span>
                          </div>

                          {volumeDiscountTotalSavings > 0 && (
                            <div className="flex justify-between text-sm items-center">
                              <span className="text-emerald-400 flex items-center gap-1.5">
                                <Tag className="w-3.5 h-3.5" />
                                {t.purchaseModal.volumeDiscount}
                              </span>
                              <span className="text-emerald-400 font-medium">-{currencySymbol}{volumeDiscountTotalSavings.toFixed(2)}</span>
                            </div>
                          )}

                          {quoteData.discountAmountMinor > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-green-400">{t.purchaseModal.couponDiscount}</span>
                              <span className="text-green-400 font-medium">-{currencySymbol}{quoteData.discountAmount}</span>
                            </div>
                          )}

                          {useWalletBalance && parseFloat(quoteData.walletAppliedAmount || '0') > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-amber-400 flex items-center gap-1.5">
                                <Wallet className="w-3.5 h-3.5" />
                                {t.purchaseModal.walletApplied}
                              </span>
                              <span className="text-amber-400 font-medium">-{currencySymbol}{parseFloat(quoteData.walletAppliedAmount || '0').toFixed(2)}</span>
                            </div>
                          )}

                          <div className="pt-3 border-t border-slate-600/50 flex justify-between">
                            <span className="text-base font-semibold text-white">{t.purchaseModal.total}</span>
                            <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-amber-400 to-yellow-500">
                              {useWalletBalance && quoteData.totalAmountDueAfterWallet
                                ? `${currencySymbol}${parseFloat(quoteData.totalAmountDueAfterWallet).toFixed(2)}`
                                : `${currencySymbol}${quoteData.totalAmountAfterDiscount}`}
                            </span>
                          </div>

                          {useWalletBalance && quoteData.walletCanFullyCover && (
                            <div className="flex items-center gap-2 mt-2 p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                              <span className="text-xs text-emerald-300">{t.purchaseModal.walletCoversFullAmount}</span>
                            </div>
                          )}

                          {useWalletBalance && !quoteData.walletCanFullyCover && parseFloat(quoteData.walletAppliedAmount || '0') > 0 && (
                            <div className="flex items-center gap-2 mt-2 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                              <span className="text-xs text-amber-300">
                                {(t.purchaseModal.walletPartialCover || 'Remaining {symbol}{amount} will be charged via {method}')
                                  .replace('{symbol}', currencySymbol)
                                  .replace('{amount}', parseFloat(quoteData.totalAmountDueAfterWallet || '0').toFixed(2))
                                  .replace('{method}', formData.paymentMethod === 'card' ? 'Stripe' : formData.paymentMethod === 'paypal' ? 'PayPal' : 'BitPay')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Terms & Conditions */}
                      <div className="space-y-3">
                        <label className="flex items-start gap-2.5 cursor-pointer group">
                          <input
                            type="checkbox"
                            name="acceptTerms"
                            checked={formData.acceptTerms}
                            onChange={handleInputChange}
                            className="mt-0.5 w-4 h-4 rounded border-slate-600 bg-slate-800/50 text-amber-500 focus:ring-2 focus:ring-amber-500"
                          />
                          <span className="text-xs text-slate-300 group-hover:text-white transition-colors">
                            {t.purchaseModal.termsAndConditions}
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  {(!quoteData || !quoteData.isBlacklisted) && (
                    <>
                      <button
                        onClick={handlePurchase}
                        disabled={!isStep3Valid() || isProcessing || !quoteData || quoteLoading}
                        className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-slate-900 font-semibold py-2.5 md:py-3 rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm active:scale-[.98]"
                      >
                        {isProcessing ? (
                          <span className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t.purchaseModal.processing}
                          </span>
                        ) : (
                          t.purchaseModal.completePurchase
                        )}
                      </button>

                      <p className="text-[9px] md:text-[10px] text-slate-500 text-center mt-2 md:mt-3 leading-relaxed px-2">
                        {t.purchaseModal.subscriptionDisclaimer.replace('{productName}', displayProduct?.name || '')}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center min-h-[400px] md:min-h-[600px] p-6 md:p-12 relative">
            <div className="text-center relative z-10">
              <div className="inline-block mb-4 md:mb-6">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center border-2 border-green-500/50 shadow-lg shadow-green-500/20">
                  <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-green-400" />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4">{t.purchaseModal.purchaseSuccessful}</h3>
              <p className="text-sm md:text-base text-slate-300 mb-4 md:mb-6">{t.purchaseModal.purchaseSuccessMessage}</p>
              <div className="flex items-center justify-center gap-2 text-slate-400 text-xs md:text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                {t.purchaseModal.redirecting}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseModal;
